import { __ } from "@wordpress/i18n";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";
import React, { useEffect, useRef, useState } from "react";
import "./TagInput.scss";

const TagInput = ({ type = 'tag', initialTags, whitelist = [], name, handleChange }) => {
    const tagifyInputRef = useRef(null);
    const tagifyInstanceRef = useRef(null);
    const isInternalChangeRef = useRef(false);
    
    // Ensure initialTags is always an array
    const formattedInitialTags = Array.isArray(initialTags) ? initialTags.map(item => (
        item.value && item.code ? { value: item.value, code: item.code } : item
    )) : [];
    
    const [currentTags, setCurrentTags] = useState(formattedInitialTags);

    // Regex for validating IPv4 address and Email
    const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Initialize Tagify once on component mount
    useEffect(() => {
        tagifyInstanceRef.current = new Tagify(tagifyInputRef.current, {
            whitelist: whitelist.map(item => (
                item.value && item.code ? { value: item.value, code: item.code } : item
            )),
            dropdown: {
                maxItems: whitelist.length,
                enabled: 0,
            },
            originalInputValueFormat: valuesArr => JSON.stringify(valuesArr),
        });

        // Set up event listeners
        const handleTagChange = (e) => {
            let parsedTags;
            try {
                parsedTags = JSON.parse(e.detail.value);
            } catch (error) {
                parsedTags = [];
            }
            
            setCurrentTags(parsedTags);
            
            // Only propagate changes up if this isn't an internal update
            if (!isInternalChangeRef.current) {
                handleChange(name, parsedTags);
            }
            isInternalChangeRef.current = false;
        };

        tagifyInstanceRef.current.on("change", handleTagChange);

        // Type-specific validation
        if (type === "ip") {
            tagifyInstanceRef.current.on('add', (e) => {
                const addedTag = e.detail.data;
                if (!ipRegex.test(addedTag.value)) {
                    tagifyInstanceRef.current.removeTag(addedTag.value);
                }
            });
        } else if (type === "email") {
            tagifyInstanceRef.current.on('add', (e) => {
                const addedTag = e.detail.data;
                if (!emailRegex.test(addedTag.value)) {
                    tagifyInstanceRef.current.removeTag(addedTag.value);
                }
            });
        }

        return () => {
            if (tagifyInstanceRef.current) {
                tagifyInstanceRef.current.destroy();
            }
        };
    }, []); // Empty dependency array - only run once on mount

    // Update tags when initialTags prop changes
    useEffect(() => {
        if (tagifyInstanceRef.current) {
            // Prevent loops by tracking internal changes
            isInternalChangeRef.current = true;
            
            // Compare if tags have actually changed before updating
            const currentValues = tagifyInstanceRef.current.value || [];
            const currentValuesJSON = JSON.stringify(currentValues);
            const newValuesJSON = JSON.stringify(formattedInitialTags);
            
            if (currentValuesJSON !== newValuesJSON) {
                tagifyInstanceRef.current.removeAllTags();
                tagifyInstanceRef.current.addTags(formattedInitialTags);
            }
            
            isInternalChangeRef.current = false;
        }
    }, [initialTags]);

    // Handle Select All
    const handleSelectAll = () => {
        tagifyInstanceRef.current.removeAllTags();
        const allTags = whitelist.map(item => 
            typeof item === 'object' && item.value ? item : { value: item }
        );
        tagifyInstanceRef.current.addTags(allTags);
    };

    // Handle Remove All
    const handleRemoveAll = () => {
        tagifyInstanceRef.current.removeAllTags();
    };

    return (
        <div className="tagify-wrapper">
            <input 
                type="text" 
                ref={tagifyInputRef} 
                placeholder={`Add ${type}...`}
            />
            {whitelist.length > 0 && (
                <div className="tagify-button-group mt-2">
                    <button 
                        type="button" 
                        className="tagify-button select-all-buton pl-heading-1 fw-600 text-purple-40" 
                        onClick={handleSelectAll}
                    >
                        {__("Select All", "ultimate-security")}
                    </button>
                    <button 
                        type="button" 
                        className="tagify-button remove-all-button pl-heading-1 fw-600 text-extra" 
                        onClick={handleRemoveAll}
                    >
                        {__("Remove All", "ultimate-security")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default TagInput;